import React, { useEffect, useMemo, useState } from "react";
import { IonIcon } from "@ionic/react";
import styled from "styled-components";
import { cardSurface } from "../../components/atoms/Card";
import QuizIcon from "../../assets/MentalPlus/Icone-interrogation.svg";
import evolutionIcon from "../../assets/MentalPlus/NavbarIcons/evolutionicon.svg";
import {
  QuestionnaireCardAction,
  QuestionnaireCardConfig,
} from "./IslandRegistry";
import {
  getScoreBand,
  getScoreColorFromVisual,
  getScoreGradientFromVisual,
  getScorePercent,
} from "./questionnaireCardUtils";

type IslandQuestionnaireCardMode = "launch" | "result" | "unavailable";

type Props = {
  mode: IslandQuestionnaireCardMode;
  config: QuestionnaireCardConfig;
  score?: number | null;
  nextAvailabilityText?: string;
  onQuestionnaire?: () => void;
  onEvolution?: () => void;
};

const CursorIcon = ({
  width = 28,
  height = 28,
}: {
  width?: number;
  height?: number;
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M20.05 17.65C20.4226 17.3706 20.725 17.0082 20.9333 16.5916C21.1416 16.1751 21.25 15.7157 21.25 15.25V4.25C21.25 3.45435 20.9339 2.69129 20.3713 2.12868C19.8087 1.56607 19.0456 1.25 18.25 1.25H6.25C5.45435 1.25 4.69129 1.56607 4.12868 2.12868C3.56607 2.69129 3.25 3.45435 3.25 4.25V15.25C3.25 15.7157 3.35844 16.1751 3.56672 16.5916C3.775 17.0082 4.07741 17.3706 4.45 17.65L10.45 22.15C10.9693 22.5395 11.6009 22.75 12.25 22.75C12.8991 22.75 13.5307 22.5395 14.05 22.15L20.05 17.65Z"
      fill="currentColor"
    />
  </svg>
);

const HeaderActionButton = ({
  action,
  onEvolution,
}: {
  action?: QuestionnaireCardAction;
  onEvolution?: () => void;
}) => {
  if (!action || action.kind === "none") {
    return null;
  }

  if (action.kind === "evolution") {
    return (
      <ActionPillButton onClick={onEvolution} type="button">
        {action.icon === "graph" && <IonIcon src={evolutionIcon} />}
        <span>{action.label}</span>
      </ActionPillButton>
    );
  }

  return null;
};

const IslandQuestionnaireCard: React.FC<Props> = ({
  mode,
  config,
  score,
  nextAvailabilityText,
  onQuestionnaire,
  onEvolution,
}) => {
  const [animatedCursorPercent, setAnimatedCursorPercent] = useState(0);

  const scoreVisual = config.scoreVisual;
  const scorePercent =
    score != null && scoreVisual ? getScorePercent(score, scoreVisual) : 0;
  const displayedScorePercent = scoreVisual?.invertBarDirection
    ? 100 - scorePercent
    : scorePercent;
  const scoreColor =
    score != null ? getScoreColorFromVisual(score, scoreVisual) : "#FF5722";
  const scoreGradient = getScoreGradientFromVisual(scoreVisual);

  const scoreBand = useMemo(() => {
    if (score == null) {
      return null;
    }

    return getScoreBand(score, config.scoreBands);
  }, [config.scoreBands, score]);

  const launchAriaLabel =
    config.launchAction && config.launchAction.kind !== "none"
      ? config.launchAction.label
      : config.launchTitle;

  useEffect(() => {
    setAnimatedCursorPercent(0);

    if (score == null || mode !== "result") {
      return;
    }

    const timer = setTimeout(() => {
      setAnimatedCursorPercent(displayedScorePercent);
    }, 150);

    return () => clearTimeout(timer);
  }, [displayedScorePercent, mode, score]);

  if (mode === "launch") {
    return (
      <LaunchCard
        aria-label={launchAriaLabel}
        large
        onClick={onQuestionnaire}
        type="button"
      >
        <LeftIcon>
          <CardIcon src={QuizIcon} alt="quiz" />
        </LeftIcon>
        <CardCenterText large>{config.launchTitle}</CardCenterText>
        <RightArrow>›</RightArrow>
      </LaunchCard>
    );
  }

  if (mode === "result" && score != null) {
    return (
      <ResultCard>
        <ResultHeader>
          <ResultTitle>{config.resultTitle ?? config.launchTitle}</ResultTitle>
          <HeaderActionButton action={config.resultAction} onEvolution={onEvolution} />
        </ResultHeader>

        {scoreVisual && (
          <ResultBar $gradient={scoreGradient}>
            <CursorWrapper
              style={{
                left: `${animatedCursorPercent}%`,
                color: scoreColor,
              }}
            >
              <CursorIcon width={28} height={28} />
            </CursorWrapper>
          </ResultBar>
        )}

        {!config.hideResultText && (
          <>
            <ResultText>{scoreBand?.message}</ResultText>
            {scoreBand?.subMessage && (
              <ResultSubText>{scoreBand.subMessage}</ResultSubText>
            )}
          </>
        )}
        {config.unavailableMessage === "next-availability" && nextAvailabilityText && (
          <ResultMetaText>{nextAvailabilityText}</ResultMetaText>
        )}
      </ResultCard>
    );
  }

  return (
    <ResultCard>
      <ResultHeader>
        <ResultTitle>{config.launchTitle}</ResultTitle>
        <HeaderActionButton action={config.unavailableAction} onEvolution={onEvolution} />
      </ResultHeader>

      {config.unavailableMessage === "next-availability" && nextAvailabilityText && (
        <ResultSubText>{nextAvailabilityText}</ResultSubText>
      )}
    </ResultCard>
  );
};

export default IslandQuestionnaireCard;

const CardIcon = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
  flex-shrink: 0;
`;

const LaunchCard = styled.button<{ large?: boolean }>`
  width: 100%;
  min-height: ${({ large }) => (large ? "88px" : "68px")};
  background: #ffffff;
  ${cardSurface}
  padding: 0 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  position: relative;

  &:active {
    transform: scale(0.98);
  }
`;

const LeftIcon = styled.div`
  width: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  z-index: 1;
`;

const CardCenterText = styled.div<{ large?: boolean }>`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  font-size: ${({ large }) => (large ? "1.05rem" : "0.95rem")};
  font-weight: 700;
  color: #111;
  text-align: center;
`;

const RightArrow = styled.div`
  font-size: 3.2rem;
  line-height: 1;
  color: #4285f4;
  flex-shrink: 0;
  z-index: 1;
  transform: translateY(-2px);
`;

const ResultCard = styled.div`
  width: 100%;
  max-width: 340px;
  background: white;
  ${cardSurface}
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  animation: fadeInUp 0.45s ease;

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(12px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ResultHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

const ResultTitle = styled.div`
  font-size: 1.05rem;
  font-weight: 700;
  color: #111;
`;

const ActionPillButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 92px;
  height: 32px;
  border: none;
  border-radius: 999px;
  padding: 0 12px;
  background: #4285f4;
  color: white;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;

  ion-icon {
    font-size: 15px;
    color: inherit;
  }

  &:active {
    transform: scale(0.98);
  }
`;

const ResultBar = styled.div<{ $gradient: string }>`
  width: 100%;
  height: 10px;
  border-radius: 10px;
  background: ${({ $gradient }) => $gradient};
  position: relative;
  margin-top: 27px;
`;

const CursorWrapper = styled.div`
  position: absolute;
  top: -30px;
  transform: translateX(-50%);
  transition: left 0.9s cubic-bezier(0.22, 1, 0.36, 1), color 0.35s ease;
  pointer-events: none;
  z-index: 2;

  svg {
    width: 28px;
    height: 28px;
    display: block;
    transition: fill 0.35s ease, stroke 0.35s ease, transform 0.25s ease;
    filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.12));
  }

  svg * {
    fill: currentColor !important;
    stroke: currentColor !important;
  }
`;

const ResultText = styled.div`
  font-size: 0.95rem;
  color: #333;
  line-height: 1.4;
`;

const ResultSubText = styled.div`
  font-size: 0.9rem;
  color: #666;
  line-height: 1.4;
`;

const ResultMetaText = styled.div`
  font-size: 0.82rem;
  color: #4285f4;
  line-height: 1.4;
  font-weight: 600;
`;